import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";

import RatingSummary from "./reviews/RatingSummary";
import ReviewCard from "./reviews/ReviewCard";
import ReviewFilters from "./reviews/ReviewFilters";
import EmptyReviews from "./reviews/EmptyReviews";
import { useMerchantReviews } from "@/hooks";
import { ReviewType } from "@/interfaces/review";

interface MerchantReviewsProps {
  merchantId: string;
}

type ReviewFilter = "all" | "positive" | "negative";

export default function MerchantReviews({ merchantId }: MerchantReviewsProps) {
  const [isLoading, setisLoading] = useState<boolean>(false);

  const [page, setPage] = useState(1);
  const [isActive, setIsActive] = useState<ReviewFilter>("all");

  const [openReview, setOpenReview] = useState(false);

  const { data: merchantReview } = useMerchantReviews(merchantId);

  const reviews = merchantReview?.data ?? [];

  const pagination = merchantReview?.meta;

  const filteredReviews = useMemo(() => {
    switch (isActive) {
      case "positive":
        return reviews.filter((review) => review.type === ReviewType.POSITIVE);

      case "negative":
        return reviews.filter((review) => review.type === ReviewType.NEGATIVE);

      default:
        return reviews;
    }
  }, [reviews, isActive]);

  return (
    <div className="space-y-6">
      <RatingSummary summary={merchantReview?.summary} />

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <ReviewFilters
          summary={merchantReview?.summary}
          isActive={isActive}
          setIsActive={setIsActive}
        />
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-36 rounded-2xl animate-pulse bg-muted" />
          ))}
        </div>
      ) : filteredReviews.length ? (
        <div className="space-y-4">
          {filteredReviews.map((review: any) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      ) : (
        <EmptyReviews onLeaveReview={() => setOpenReview(true)} />
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>

          <Button
            variant="outline"
            disabled={page === pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
